// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { AppIcon, defineComponent, IconButton } from '@shared/ui-kit';
import { Translation } from '@shared/ui-kit/providers/translation-provider';
import { Button, Tooltip, Typography } from '@mui/material';
import styles from './banks-list.module.css';
import { useInstitutionSyncStatus } from './use-institution-sync-status';
import { ReconnectAccountButton } from '../reconnect-account-button/reconnect-account-button';
import { ShowInAppAdvisor, ShowInAppClient } from '@shared/ui-kit/utils/app-utils';

export const InstitutionSyncStatus = defineComponent({
  displayName: 'InstitutionSyncStatus',
  useSetup: useInstitutionSyncStatus,
  View: ({ institution, onClick, lastSyncFormatted, errorType, t, onSync }) => (
    <>
      <Button
        variant="text"
        size="small"
        onClick={onClick}
        className={styles['sync-button']}
        data-variant={errorType}
        color={
          errorType === 'success'
            ? 'success'
            : errorType === 'error'
            ? 'error'
            : 'warning'
        }
      >
        <Typography variant="caption" className={styles.caption}>
          {errorType === 'success' && (
            <AppIcon
              icon="CircleCheck"
              color="success"
              size="lg"
              className={styles['sync-icon']}
            />
          )}
          {errorType === 'error' && (
            <AppIcon
              icon="AlertTriangle"
              color="danger"
              size="lg"
              className={styles['sync-icon']}
            />
          )}
          {errorType === 'warning' && (
            <AppIcon
              icon="RefreshTwoArrows"
              color="warning"
              size="lg"
              className={styles['sync-icon']}
            />
          )}
          {errorType === 'warning' ? (
            <>
              <ShowInAppClient>
                <ReconnectAccountButton institution={institution.id} />
              </ShowInAppClient>
              <ShowInAppAdvisor>
                {institution.last_sync ? (
                  <span className={styles['sync-text']}>{lastSyncFormatted}</span>
                ) : (
                  <span className={styles['sync-text']}>
                    <Translation
                      i18nKey="aggregator.sync-account"
                      ns="aggregator"
                    />
                  </span>
                )}
              </ShowInAppAdvisor>
            </>
          ) : institution.last_sync ? (
            <span className={styles['sync-text']}>{lastSyncFormatted}</span>
          ) : (
            <span className={styles['sync-text']}>
              <Translation i18nKey="aggregator.sync-account" ns="aggregator" />
            </span>
          )}
        </Typography>
      </Button>
      {errorType !== 'warning' && (
        <Tooltip title={t('common.refresh', { ns: 'common' })}>
          <IconButton
            icon="RefreshTwoArrows"
            variant="icon"
            color="main"
            size="lg"
            className={styles['refresh-button']}
            onClick={onSync}
          />
        </Tooltip>
      )}
    </>
  ),
});
